import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCounts } from 'src/domain/analytics';
import { PenIcon } from 'src/components/Icons/icons';
import { Button } from 'src/components/Buttons/Button';
import { AnalyticsCard } from './AnalyticsCard';
import { MetricCards } from './MetricCards';
import {
    DEFAULT_MINUTES_PER_MESSAGE,
    estimatedHours,
    parseMinutes,
} from './timeEstimate';

export const TimeFreedCard = ({
    messages,
    customMinutes,
    onChange,
}: {
    messages: MessageCounts;
    customMinutes: number | null;
    onChange: (minutes: number | null) => void;
}) => {
    const { t, i18n } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const minutes = customMinutes ?? DEFAULT_MINUTES_PER_MESSAGE;
    const save = (event: FormEvent) => {
        event.preventDefault();
        const value = parseMinutes(draft);
        if (value === undefined) return;
        onChange(value);
        setEditing(false);
    };
    return (
        <AnalyticsCard
            title={t('admin.analytics.timeFreedTitle')}
            description={t('admin.analytics.timeFreedScope')}
            info={t('admin.analytics.timeFreedCalculation')}
            className={`analytics-time-freed${
                customMinutes === null ? ' analytics-time-freed-default' : ''
            }`}
            action={
                <button
                    type="button"
                    className="analytics-edit-assumption"
                    aria-label={t('admin.analytics.editAssumption')}
                    onClick={() => {
                        setDraft(String(minutes));
                        setEditing(true);
                    }}
                >
                    <PenIcon />
                </button>
            }
        >
            <p className="analytics-assumption-label">
                {t(
                    customMinutes === null
                        ? 'admin.analytics.defaultAssumption'
                        : 'admin.analytics.customAssumption',
                    { minutes },
                )}
            </p>
            <MetricCards
                metrics={[
                    {
                        label: t('admin.analytics.estimatedTimeFreed'),
                        value: t('admin.analytics.hours', {
                            value: estimatedHours(
                                messages,
                                minutes,
                            ).toLocaleString(i18n.language, {
                                maximumFractionDigits: 1,
                            }),
                        }),
                    },
                ]}
            />
            {editing && (
                <form className="analytics-assumption-form" onSubmit={save}>
                    <label>
                        {t('admin.analytics.minutesPerMessage')}
                        <input
                            autoFocus
                            type="number"
                            min="0"
                            step="any"
                            required
                            className="text-input"
                            value={draft}
                            onChange={event => setDraft(event.target.value)}
                        />
                    </label>
                    <div className="analytics-assumption-actions">
                        <Button
                            title={t('admin.analytics.applyAssumption')}
                            submit
                            disabled={parseMinutes(draft) === undefined}
                        />
                        <button type="button" onClick={() => setEditing(false)}>
                            {t('admin.analytics.cancelAssumption')}
                        </button>
                    </div>
                </form>
            )}
            {customMinutes !== null && !editing && (
                <button
                    className="analytics-reset-assumption"
                    type="button"
                    onClick={() => onChange(null)}
                >
                    {t('admin.analytics.resetAssumption')}
                </button>
            )}
        </AnalyticsCard>
    );
};
